import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";

// --- Skeleton Loading Imports ---
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css"; // Import skeleton styles


import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

function News() {
  const [slidesPerView, setSlidesPerView] = useState(5);
  const [activeSlide, setActiveSlide] = useState(0);
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isLightMode = document.body.classList.contains("light-mode");

  // --- Responsive SlidesPerView Logic (No changes needed here) ---
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setSlidesPerView(1);
      } else if (window.innerWidth < 768) {
        setSlidesPerView(2);
      } else if (window.innerWidth < 1024) {
        setSlidesPerView(3);
      } else {
        setSlidesPerView(5);
      }
    };

    handleResize(); // Call initially
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- Fetch News Logic ---
  const fetchNews = async () => {
    setLoading(true); // Ensure loading is true at the start of fetch
    setError(null); // Clear previous errors

    try {
      // Use serverless function to avoid CORS issues
      const response = await fetch('/api/news');
      if (!response.ok) {
        throw new Error(`Failed to fetch news (status: ${response.status})`);
      }
      const data = await response.json();
      // Check if articles exist and is an array
      if (data && Array.isArray(data.articles)) {
        setNewsItems(data.articles);
      } else {
        // Handle cases where articles might be missing or not an array
        console.warn(
          "API response format unexpected or articles missing:",
          data
        );
        setNewsItems([]); // Set to empty array to avoid errors later
        // Optionally, set an error state here if this is considered an error condition
        // setError("No news articles found in the response.");
      }
    } catch (error) {
      console.error("Error fetching news:", error); // Log the actual error
      setError(error.message);
    } finally {
      setLoading(false); // Set loading to false whether fetch succeeded or failed
    }
  };

  useEffect(() => {
    fetchNews();
  }, []); // Fetch news on component mount

  // --- **Skeleton Loading UI** ---
  if (loading) {
    // We reuse the main layout structure for consistency
    return (
      <div className={`min-h-screen flex flex-col relative overflow-hidden ${isLightMode ? "text-black" : "text-white"}`}>
        <div className="background-circle circle-3 z-0"></div>
        <div className="background-circle circle-4 z-0"></div>
        <div className="z-50">
          <Navbar />
        </div>
        <div className="relative bg-transparent py-4 mb-6">
          {/* Skeleton doesn't need the back link logic, but keep spacing */}
          <div className="absolute md:ml-32 ml-8 top-10 -translate-y-1/2 opacity-0">
            {" "}
            {/* Keep space */}
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </div>
        </div>
        <main className="flex-1 container mx-auto px-4 sm:px-6 sm:py-8 max-w-6xl relative z-10 sm:mt-5">
          {/* Wrap skeletons in SkeletonTheme for consistent styling (optional, but good for themes) */}
          {/* Adjust baseColor and highlightColor to match your dark theme */}
          <SkeletonTheme
  baseColor={isLightMode ? "#d1d5db" : "#333"}
  highlightColor={isLightMode ? "#e5e7eb" : "#555"}
>
            <div className="text-center">
              <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-8">
                <Skeleton width={150} /> {/* Skeleton for "Latest News" */}
              </h1>
            </div>

            {/* Skeleton for Swiper */}
            <div className="mb-4 sm:mb-8 max-w-5xl mx-auto">
              <div className="flex space-x-[10px] justify-center">
                {" "}
                {/* Mimic Swiper structure */}
                {/* Render number of skeletons based on default slidesPerView */}
                {[...Array(5)].map((_, index) => (
                  <Skeleton
                    key={index}
                    className="w-80 h-24 sm:h-32 md:h-52 rounded-lg"
                  />
                ))}
              </div>
            </div>

            {/* Skeleton for Active Slide Title */}
            <div className="mb-4 sm:mb-8 text-center max-w-3xl mx-auto px-4">
              <h2 className="text-base sm:text-xl font-semibold">
                {/* Use multiple lines for title skeleton */}
                <Skeleton
                  height={20}
                  width={`70%`}
                  style={{ margin: "0 auto 0.25rem" }}
                />
                <Skeleton
                  height={20}
                  width={`50%`}
                  style={{ margin: "0 auto" }}
                />
              </h2>
            </div>

            {/* Skeleton for News List */}
            <div className="space-y-3 sm:space-y-6 max-w-3xl mx-auto px-4">
              {[...Array(6)].map(
                (
                  _,
                  index // Render 6 placeholder list items
                ) => (
                  <div
                    key={index}
                    className="flex gap-2 sm:gap-4 items-center p-2 sm:p-3 rounded-lg"
                  >
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                      {/* Use height="100%" to fill the container or fixed height/width */}
                      <Skeleton className="w-full h-full rounded-lg" />
                    </div>
                    <div className="flex-1">
                      {/* Skeleton for title lines */}
                      <Skeleton count={1} height={16} />
                    </div>
                  </div>
                )
              )}
            </div>
          </SkeletonTheme>
        </main>
        <Footer />
      </div>
    );
  }
  // --- **End Skeleton Loading UI** ---

  if (error) {
    // Keep existing error handling, maybe make it more prominent
    return (
      <div className="min-h-screen text-white flex flex-col relative overflow-hidden items-center justify-center">
        <div className="background-circle circle-3 z-0"></div>
        <div className="background-circle circle-4 z-0"></div>
        <div className="z-50 fixed top-0 left-0 right-0">
          <Navbar />
        </div>
        <div className="text-center p-8 bg-red-800/50 rounded-lg z-10">
          <h2 className="text-2xl mb-4">Error Loading News</h2>
          <p className="text-lg">{error}</p>
          <p className="mt-4">
            Please try refreshing the page or check your connection.
          </p>
          {/* Optional: Add a refresh button */}
          <button
            onClick={fetchNews}
            className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
        <div className="fixed bottom-0 left-0 right-0">
          <Footer />
        </div>
      </div>
    );
  }

  // --- **Original Content Render (when not loading and no error)** ---
  return (
    <div className={`min-h-screen flex flex-col relative overflow-hidden ${isLightMode ? "text-black" : "text-white"}`}>
      <div className="background-circle circle-3 z-0"></div>
      <div className="background-circle circle-4 z-0"></div>
      <div className="z-50">
        <Navbar />
      </div>
      <div className="relative bg-transparent py-4 mb-6">
        {/* Back link - remains the same */}
        <Link
          to="/portal/Dashboard"
          className="absolute md:ml-32 ml-8 top-10 -translate-y-1/2"
        >
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </Link>
      </div>
      <main className="flex-1 container mx-auto px-4 sm:px-6 sm:py-8 max-w-6xl relative z-10 sm:mt-5">
        <div className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-8">
            Latest News
          </h1>
        </div>

        {/* Swiper - remains the same */}
        <div className="mb-4 sm:mb-8 max-w-5xl mx-auto">
          {newsItems.length > 0 ? ( // Only render Swiper if there are items
            <Swiper
              slidesPerView={slidesPerView}
              spaceBetween={10}
              navigation={true}
              loop={newsItems.length > slidesPerView} // Only loop if enough items
              centeredSlides={true}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
              modules={[Navigation, Pagination, Autoplay]}
              className="w-full"
            >
              {newsItems.map((item, index) => (
                <SwiperSlide key={item.url || index}>
                  {" "}
                  {/* Use a unique key like item.url if available */}
                  <div className="relative cursor-pointer group">
                    <img
                      src={item.image || "/images/default-news.png"} // Provide a path to a default image in your public folder
                      alt={item.title || "News article image"}
                      className="w-80 h-24 sm:h-32 md:h-52 object-cover rounded-lg bg-gray-700" // Added background color for loading/missing images
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/default-news.png";
                      }} // Fallback on image load error
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 h-full rounded-lg">
                      <p className="absolute bottom-2 left-2 right-2 text-[10px] sm:text-xs md:text-sm text-white line-clamp-2">
                        {item.title}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <div className="text-center text-gray-400 py-10">
              No news articles found.
            </div> // Message if no news
          )}
        </div>

        {/* Active Slide Title - remains the same, add check for item existence */}
        {newsItems.length > 0 && newsItems[activeSlide] && (
          <div className="mb-4 sm:mb-8 text-center max-w-3xl mx-auto px-4">
            <h2 className="text-base sm:text-xl font-semibold line-clamp-2 sm:line-clamp-none">
              {newsItems[activeSlide]?.title}
            </h2>
          </div>
        )}

        {/* News List - remains the same, add check for item existence */}
        {newsItems.length > 0 && (
          <div className="space-y-3 sm:space-y-6 max-w-3xl mx-auto px-4">
            {newsItems.map((item, index) => (
              <a // Wrap list item in an anchor tag to make it clickable
                key={item.url || index} // Use a unique key
                href={item.url}
                target="_blank" // Open link in a new tab
                rel="noopener noreferrer" // Security best practice for target="_blank"
                className="flex gap-2 sm:gap-4 items-center hover:bg-white/5 p-2 sm:p-3 rounded-lg transition-colors cursor-pointer"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0">
                  <img
                    src={item.image || "/images/default-news.png"} // Default image path
                    alt={item.title || "News article image"}
                    className="w-full h-full object-cover rounded-lg bg-gray-700" // Added background color
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/images/default-news.png";
                    }} // Fallback
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-xs sm:text-sm font-medium line-clamp-2 sm:line-clamp-none">
                    {item.title}
                  </h3>
                  {/* Optionally add source or published time */}
                  {item.source?.name && (
                    <p className="text-xs text-gray-400 mt-1">
                      {item.source.name}
                    </p>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export default News;
